import { useLastSongSelector, useWaitingListSelector } from "@redux/player";
import { sessionEndTimestampSelector } from "@redux/session/selectors";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { SongWithSinger } from "shared-types/song";

export const useWaitingListData = () => {
	const waitingList = useWaitingListSelector();

	const sessionEndTimeStamp = useSelector(sessionEndTimestampSelector);
	const lastSong = useLastSongSelector();

	const waitingListSongDuration = useCallback(() => {
		return waitingList.reduce(
			(acc, song) => song?.length && acc + (Number(song.length) || 0) * 1000,
			0,
		);
	}, [waitingList]);

	const waitingListDuration = waitingListSongDuration();

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const splitWaitingList = useMemo(() => {
		const playlableTime =
			(sessionEndTimeStamp ? sessionEndTimeStamp - Date.now() : 0) -
			(lastSong?.song?.length || 0) * 1000;

		const playableWaitingList: { song: SongWithSinger; globalIndex: number }[] =
			[];
		const elapsedTimeWaitingList: {
			song: SongWithSinger;
			globalIndex: number;
		}[] = [];

		let elapsedTime = playlableTime;

		for (const [i, song] of waitingList.entries()) {
			if (!song) continue;
			const songDuration = song.length ? Number(song.length) * 1000 : 0;

			if (elapsedTime >= 0 && elapsedTime >= songDuration) {
				playableWaitingList.push({ song, globalIndex: i });
			} else {
				elapsedTimeWaitingList.push({ song, globalIndex: i });
			}

			elapsedTime -= songDuration;
		}

		return {
			playableWaitingList,
			elapsedTimeWaitingList,
			waitingListDuration,
		};
	}, [waitingList, sessionEndTimeStamp, lastSong, waitingListDuration]);

	return {
		waitingList,
		splitWaitingList,
		waitingListDuration,
		sessionEndTimeStamp,
	};
};
